import axios from 'axios';

const TLD_WHITELIST_URL = 'https://cdn.spotandtango.com/tld-whitelist.csv';
const FETCH_TIMEOUT_MS = 5000;
const MIN_TLD_WHITELIST_SIZE = 500;
// Backoff schedule for automatic retries right after a failed load.
const RETRY_DELAYS_MS = [1000, 5000, 15000];
// Once the automatic retries above are exhausted, isTldAllowed re-attempts
// lazily at most this often, so the feature can still self-heal for as long
// as the admin keeps using the page, without needing a full reload.
const LAZY_RETRY_COOLDOWN_MS = 30000;

let tldWhitelist: Set<string> | null = null;
let isFetching = false;
let lastAttemptAt = 0;

const scheduleRetry = (remainingDelays: number[]): void => {
  const [delay, ...rest] = remainingDelays;
  setTimeout(() => attemptFetch(rest), delay);
};

const attemptFetch = (remainingDelays: number[] = []): void => {
  if (isFetching) {
    return;
  }
  isFetching = true;
  lastAttemptAt = Date.now();

  axios
    .get<string>(TLD_WHITELIST_URL, { responseType: 'text', timeout: FETCH_TIMEOUT_MS })
    .then((response) => {
      const tlds = response.data
        .split('\n')
        .map((line) => line.trim().toUpperCase())
        .filter(Boolean);

      if (tlds.length >= MIN_TLD_WHITELIST_SIZE) {
        tldWhitelist = new Set(tlds);
      } else if (remainingDelays.length > 0) {
        scheduleRetry(remainingDelays);
      }
    })
    .catch(() => {
      if (remainingDelays.length > 0) {
        scheduleRetry(remainingDelays);
      }
    })
    .finally(() => {
      isFetching = false;
    });
};

export const preloadTldWhitelist = (): void => {
  attemptFetch(RETRY_DELAYS_MS);
};

export const isTldAllowed = (tld: string): boolean => {
  if (!tldWhitelist) {
    if (!isFetching && Date.now() - lastAttemptAt > LAZY_RETRY_COOLDOWN_MS) {
      attemptFetch();
    }
    return true;
  }
  return tldWhitelist.has(tld.toUpperCase());
};
